import { collection, deleteDoc, doc, DocumentData, DocumentReference, Firestore, getDoc, setDoc, updateDoc } from 'firebase/firestore';

export type TenantDataReadSource = 'legacy' | 'namespaced';
export type TenantDataWriteMode = 'legacy' | 'dual' | 'namespaced';

type TenantDataDomain = 'users' | 'profiles' | 'permissions';

const VALID_SOURCES: TenantDataReadSource[] = ['legacy', 'namespaced'];
const VALID_WRITE_MODES: TenantDataWriteMode[] = ['legacy', 'dual', 'namespaced'];

const normalizeReadSource = (value: string | undefined): TenantDataReadSource => {
  const normalized = String(value || '').trim().toLowerCase();
  return VALID_SOURCES.includes(normalized as TenantDataReadSource)
    ? (normalized as TenantDataReadSource)
    : 'legacy';
};

const readLocalOverride = (): TenantDataReadSource | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('aspis_firestore_read_source');
    return raw ? normalizeReadSource(raw) : null;
  } catch {
    return null;
  }
};

const normalizeWriteMode = (value: string | undefined): TenantDataWriteMode => {
  const normalized = String(value || '').trim().toLowerCase();
  return VALID_WRITE_MODES.includes(normalized as TenantDataWriteMode)
    ? (normalized as TenantDataWriteMode)
    : 'dual';
};

const readLocalWriteOverride = (): TenantDataWriteMode | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('aspis_firestore_write_mode');
    return raw ? normalizeWriteMode(raw) : null;
  } catch {
    return null;
  }
};

export const getTenantDataReadSource = (_domain?: TenantDataDomain): TenantDataReadSource => {
  const localOverride = readLocalOverride();
  if (localOverride) return localOverride;
  return normalizeReadSource(import.meta.env.VITE_FIRESTORE_TENANT_DATA_READ_SOURCE);
};

export const getTenantDataWriteMode = (_domain?: TenantDataDomain): TenantDataWriteMode => {
  const localOverride = readLocalWriteOverride();
  if (localOverride) return localOverride;
  return normalizeWriteMode(import.meta.env.VITE_FIRESTORE_TENANT_DATA_WRITE_MODE);
};

export const tenantNamespaceRootDoc = (db: Firestore, tenantId: string) => doc(db, 'tenants', tenantId);

const legacyTenantProfileDocRef = (db: Firestore, tenantId: string) => doc(db, 'tenant_profiles', tenantId);
const namespacedTenantProfileDocRef = (db: Firestore, tenantId: string) => doc(db, 'tenants', tenantId, 'profile', 'config');

const legacyTenantPermissionsMatrixDocRef = (db: Firestore, tenantId: string) => doc(db, 'tenant_permissions_matrix', tenantId);
const namespacedTenantPermissionsMatrixDocRef = (db: Firestore, tenantId: string) => doc(db, 'tenants', tenantId, 'profile', 'permissions');

const legacyUserDocRef = (db: Firestore, userId: string) => doc(db, 'users', userId);
const namespacedUserDocRef = (db: Firestore, tenantId: string, userId: string) => doc(db, 'tenants', tenantId, 'people', userId);

export const tenantProfileDocRef = (db: Firestore, tenantId: string) => {
  return getTenantDataReadSource('profiles') === 'namespaced'
    ? namespacedTenantProfileDocRef(db, tenantId)
    : legacyTenantProfileDocRef(db, tenantId);
};

export const tenantPermissionsMatrixDocRef = (db: Firestore, tenantId: string) => {
  return getTenantDataReadSource('permissions') === 'namespaced'
    ? namespacedTenantPermissionsMatrixDocRef(db, tenantId)
    : legacyTenantPermissionsMatrixDocRef(db, tenantId);
};

export const userDocRef = (db: Firestore, userId: string, tenantId?: string) => {
  return getTenantDataReadSource('users') === 'namespaced' && tenantId
    ? namespacedUserDocRef(db, tenantId, userId)
    : legacyUserDocRef(db, userId);
};

export const usersCollectionRef = (db: Firestore, tenantId?: string) => {
  return getTenantDataReadSource('users') === 'namespaced' && tenantId
    ? collection(db, 'tenants', tenantId, 'people')
    : collection(db, 'users');
};

export const userNotificationsCollectionRef = (db: Firestore, userId: string, tenantId?: string) => {
  return getTenantDataReadSource('users') === 'namespaced' && tenantId
    ? collection(db, 'tenants', tenantId, 'people', userId, 'notifications')
    : collection(db, 'users', userId, 'notifications');
};

export const userCpdCollectionRef = (db: Firestore, userId: string, tenantId?: string) => {
  return getTenantDataReadSource('users') === 'namespaced' && tenantId
    ? collection(db, 'tenants', tenantId, 'people', userId, 'cpd')
    : collection(db, 'users', userId, 'cpd');
};

interface MirroredWriteTargets {
  primary: DocumentReference<DocumentData>;
  mirrors: DocumentReference<DocumentData>[];
}

const resolveWriteTargets = (
  legacyRef: DocumentReference<DocumentData>,
  namespacedRef: DocumentReference<DocumentData>
): MirroredWriteTargets => {
  const mode = getTenantDataWriteMode();

  if (mode === 'namespaced') {
    return { primary: namespacedRef, mirrors: [legacyRef] };
  }

  if (mode === 'legacy') {
    return { primary: legacyRef, mirrors: [] };
  }

  return { primary: legacyRef, mirrors: [namespacedRef] };
};

const mirrorSetDoc = async (
  targets: MirroredWriteTargets,
  data: any,
  options?: any
) => {
  await setDoc(targets.primary, data, options);
  const mirrorResults = await Promise.allSettled(targets.mirrors.map((ref) => setDoc(ref, data, options)));
  mirrorResults.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.warn('[firestorePaths] mirror setDoc failed', {
        mirrorIndex: index,
        reason: result.reason,
      });
    }
  });
};

const mirrorUpdateDoc = async (
  targets: MirroredWriteTargets,
  data: any
) => {
  await updateDoc(targets.primary, data);
  const mirrorResults = await Promise.allSettled(targets.mirrors.map((ref) => setDoc(ref, data, { merge: true })));
  mirrorResults.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.warn('[firestorePaths] mirror update fallback failed', {
        mirrorIndex: index,
        reason: result.reason,
      });
    }
  });
};

const mirrorDeleteDoc = async (targets: MirroredWriteTargets) => {
  await deleteDoc(targets.primary);
  const mirrorResults = await Promise.allSettled(targets.mirrors.map((ref) => deleteDoc(ref)));
  mirrorResults.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.warn('[firestorePaths] mirror delete failed', {
        mirrorIndex: index,
        reason: result.reason,
      });
    }
  });
};

const syncMirrorsFromPrimary = async (targets: MirroredWriteTargets) => {
  if (targets.mirrors.length === 0) return;
  const snap = await getDoc(targets.primary);
  if (!snap.exists()) {
    await Promise.all(targets.mirrors.map((ref) => deleteDoc(ref)));
    return;
  }

  const mirrorResults = await Promise.allSettled(targets.mirrors.map((ref) => setDoc(ref, snap.data())));
  mirrorResults.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.warn('[firestorePaths] mirror sync failed', {
        mirrorIndex: index,
        reason: result.reason,
      });
    }
  });
};

export const setTenantProfileRecord = async (db: Firestore, tenantId: string, data: any, options?: any) => {
  await mirrorSetDoc(
    resolveWriteTargets(legacyTenantProfileDocRef(db, tenantId), namespacedTenantProfileDocRef(db, tenantId)),
    data,
    options
  );
};

export const updateTenantProfileRecord = async (db: Firestore, tenantId: string, data: any) => {
  await mirrorUpdateDoc(
    resolveWriteTargets(legacyTenantProfileDocRef(db, tenantId), namespacedTenantProfileDocRef(db, tenantId)),
    data
  );
};

export const syncTenantProfileRecordToMirrors = async (db: Firestore, tenantId: string) => {
  await syncMirrorsFromPrimary(
    resolveWriteTargets(legacyTenantProfileDocRef(db, tenantId), namespacedTenantProfileDocRef(db, tenantId))
  );
};

export const setTenantPermissionsMatrixRecord = async (db: Firestore, tenantId: string, data: any, options?: any) => {
  await mirrorSetDoc(
    resolveWriteTargets(legacyTenantPermissionsMatrixDocRef(db, tenantId), namespacedTenantPermissionsMatrixDocRef(db, tenantId)),
    data,
    options
  );
};

export const updateTenantPermissionsMatrixRecord = async (db: Firestore, tenantId: string, data: any) => {
  await mirrorUpdateDoc(
    resolveWriteTargets(legacyTenantPermissionsMatrixDocRef(db, tenantId), namespacedTenantPermissionsMatrixDocRef(db, tenantId)),
    data
  );
};

export const syncTenantPermissionsMatrixRecordToMirrors = async (db: Firestore, tenantId: string) => {
  await syncMirrorsFromPrimary(
    resolveWriteTargets(legacyTenantPermissionsMatrixDocRef(db, tenantId), namespacedTenantPermissionsMatrixDocRef(db, tenantId))
  );
};

export const setUserRecord = async (db: Firestore, userId: string, tenantId: string | undefined, data: any, options?: any) => {
  if (!tenantId) {
    await setDoc(legacyUserDocRef(db, userId), data, options);
    return;
  }

  await mirrorSetDoc(
    resolveWriteTargets(legacyUserDocRef(db, userId), namespacedUserDocRef(db, tenantId, userId)),
    data,
    options
  );
};

export const updateUserRecord = async (db: Firestore, userId: string, tenantId: string | undefined, data: any) => {
  if (!tenantId) {
    await updateDoc(legacyUserDocRef(db, userId), data);
    return;
  }

  await mirrorUpdateDoc(
    resolveWriteTargets(legacyUserDocRef(db, userId), namespacedUserDocRef(db, tenantId, userId)),
    data
  );
};

export const deleteUserRecord = async (db: Firestore, userId: string, tenantId: string | undefined) => {
  if (!tenantId) {
    await deleteDoc(legacyUserDocRef(db, userId));
    return;
  }

  await mirrorDeleteDoc(
    resolveWriteTargets(legacyUserDocRef(db, userId), namespacedUserDocRef(db, tenantId, userId))
  );
};

export const syncUserRecordToMirrors = async (db: Firestore, userId: string, tenantId: string | undefined) => {
  if (!tenantId) return;

  await syncMirrorsFromPrimary(
    resolveWriteTargets(legacyUserDocRef(db, userId), namespacedUserDocRef(db, tenantId, userId))
  );
};